import type { HqlHighlight } from '@rock/hql';
import { formatCadenceWindow, formatInterval } from '@rock/tasks';
import type { SeasonalWindow, TaskSubscription, TaskSubscriptionInterval } from '@rock/types';

/**
 * A cadence as code, field for field as it is stored: its type, then each field it has, in a fixed order.
 * `multiline` puts one field on each line, for a card.
 */
export function highlightSubscription(
    subscription: TaskSubscription,
    { multiline = false }: { multiline?: boolean } = {},
): HqlHighlight[] {
    const fields = subscriptionFields(subscription);
    const out: HqlHighlight[] = [{ text: subscription.type, role: 'keyword' }];
    if (fields.length === 0) return out;
    out.push(punctuation(multiline ? ' {\n' : ' { '));
    fields.forEach((field, index) => {
        if (index > 0) out.push(punctuation(multiline ? '\n' : ', '));
        if (multiline) out.push({ text: '  ', role: 'plain' });
        out.push({ text: field.key, role: 'field' }, punctuation(': '), ...field.value);
    });
    out.push(punctuation(multiline ? '\n}' : ' }'));
    return out;
}

/** "Recurring (1 year, after 2 months), in late summer or early fall". */
export function describeSubscription(subscription: TaskSubscription): string {
    const kind = subscription.type === 'recurring' ? 'Recurring' : 'One-time';
    const details = [
        subscription.type === 'recurring' ? formatInterval(subscription.interval) : undefined,
        subscription.offset ? `after ${formatInterval(subscription.offset)}` : undefined,
    ].filter((detail) => !!detail);
    const base = details.length ? `${kind} (${details.join(', ')})` : kind;
    const season = formatCadenceWindow(subscription);
    return season ? `${base}, in ${season}` : base;
}

type Field = { key: string; value: HqlHighlight[] };

function subscriptionFields(subscription: TaskSubscription): Field[] {
    const fields: Field[] = [];
    if (subscription.type === 'recurring') fields.push({ key: 'interval', value: interval(subscription.interval) });
    if (subscription.offset) fields.push({ key: 'offset', value: interval(subscription.offset) });
    if (subscription.window?.length) fields.push({ key: 'window', value: seasonParts(subscription.window) });
    return fields;
}

function interval({ value, unit }: TaskSubscriptionInterval): HqlHighlight[] {
    return [{ text: `${value}`, role: 'number' }, punctuation(' '), { text: unit, role: 'value' }];
}

function seasonParts(parts: SeasonalWindow): HqlHighlight[] {
    const stretches = parts.flatMap((part, index): HqlHighlight[] => [
        ...(index > 0 ? [punctuation(', ')] : []),
        { text: part, role: 'string' },
    ]);
    return [punctuation('['), ...stretches, punctuation(']')];
}

function punctuation(text: string): HqlHighlight {
    return { text, role: 'punctuation' };
}
